import { Component, Input, Output, EventEmitter, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrdersService } from '../../../shared/services/order.service';
import { ShippingConfigService, ShippingCountry } from '../../../core/services/shipping-config.service';
import { take } from 'rxjs';

interface CountryCode {
  code: string;
  name: string;
  flag: string;
}

@Component({
  selector: 'app-quotation-request-form',
  standalone: false,
  templateUrl: './quotation-request-form.component.html',
  styleUrls: ['./quotation-request-form.component.scss']
})
export class QuotationRequestFormComponent implements OnInit {
  @Input() product: any;
  @Input() productImage: string | null = null;
  @Input() showModal: boolean = false;
  @Input() standalone: boolean = false; // New property to use form without modal wrapper
  @Output() closeModalEmitter = new EventEmitter<void>();
  @Output() submitQuotationEmitter = new EventEmitter<any>();
  @Output() formSubmit = new EventEmitter<any>();

  quotationForm!: FormGroup;
  selectedFiles: File[] = [];
  isSubmitting: boolean = false;
  isDetectingLocation: boolean = false;

  // Country code selection properties
  showCountryDropdown: boolean = false;
  countrySearchTerm: string = '';
  isDeliveryCountryDropdownOpen = false;
  deliveryCountrySearchTerm = '';
  selectedCountryCode: CountryCode = { code: '', name: 'Select country', flag: '🌐' };

  availableCountries: ShippingCountry[] = [];

  countries: CountryCode[] = [
    { code: '+1', name: 'United States', flag: '🇺🇸' },
    { code: '+1', name: 'Canada', flag: '🇨🇦' },
    { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+39', name: 'Italy', flag: '🇮🇹' },
    { code: '+34', name: 'Spain', flag: '🇪🇸' },
    { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
    { code: '+32', name: 'Belgium', flag: '🇧🇪' },
    { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
    { code: '+43', name: 'Austria', flag: '🇦🇹' },
    { code: '+351', name: 'Portugal', flag: '🇵🇹' },
    { code: '+30', name: 'Greece', flag: '🇬🇷' },
    { code: '+47', name: 'Norway', flag: '🇳🇴' },
    { code: '+46', name: 'Sweden', flag: '🇸🇪' },
    { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    { code: '+358', name: 'Finland', flag: '🇫🇮' },
    { code: '+354', name: 'Iceland', flag: '🇮🇸' },
    { code: '+353', name: 'Ireland', flag: '🇮🇪' },
    { code: '+7', name: 'Russia', flag: '🇷🇺' },
    { code: '+48', name: 'Poland', flag: '🇵🇱' },
    { code: '+420', name: 'Czech Republic', flag: '🇨🇿' },
    { code: '+421', name: 'Slovakia', flag: '🇸🇰' },
    { code: '+36', name: 'Hungary', flag: '🇭🇺' },
    { code: '+40', name: 'Romania', flag: '🇷🇴' },
    { code: '+359', name: 'Bulgaria', flag: '🇧🇬' },
    { code: '+385', name: 'Croatia', flag: '🇭🇷' },
    { code: '+386', name: 'Slovenia', flag: '🇸🇮' },
    { code: '+381', name: 'Serbia', flag: '🇷🇸' },
    { code: '+382', name: 'Montenegro', flag: '🇲🇪' },
    { code: '+387', name: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { code: '+389', name: 'North Macedonia', flag: '🇲🇰' },
    { code: '+355', name: 'Albania', flag: '🇦🇱' },
    { code: '+90', name: 'Turkey', flag: '🇹🇷' },
    { code: '+998', name: 'Uzbekistan', flag: '🇺🇿' },
    { code: '+996', name: 'Kyrgyzstan', flag: '🇰🇬' },
    { code: '+992', name: 'Tajikistan', flag: '🇹🇯' },
    { code: '+993', name: 'Turkmenistan', flag: '🇹🇲' },
    { code: '+994', name: 'Azerbaijan', flag: '🇦🇿' },
    { code: '+995', name: 'Georgia', flag: '🇬🇪' },
    { code: '+374', name: 'Armenia', flag: '🇦🇲' },
    { code: '+380', name: 'Ukraine', flag: '🇺🇦' },
    { code: '+375', name: 'Belarus', flag: '🇧🇾' },
    { code: '+370', name: 'Lithuania', flag: '🇱🇹' },
    { code: '+371', name: 'Latvia', flag: '🇱🇻' },
    { code: '+372', name: 'Estonia', flag: '🇪🇪' },
    { code: '+373', name: 'Moldova', flag: '🇲🇩' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+82', name: 'South Korea', flag: '🇰🇷' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
    { code: '+853', name: 'Macau', flag: '🇲🇴' },
    { code: '+886', name: 'Taiwan', flag: '🇹🇼' },
    { code: '+65', name: 'Singapore', flag: '🇸🇬' },
    { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', name: 'Thailand', flag: '🇹🇭' },
    { code: '+84', name: 'Vietnam', flag: '🇻🇳' },
    { code: '+63', name: 'Philippines', flag: '🇵🇭' },
    { code: '+62', name: 'Indonesia', flag: '🇮🇩' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+92', name: 'Pakistan', flag: '🇵🇰' },
    { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
    { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: '+977', name: 'Nepal', flag: '🇳🇵' },
    { code: '+975', name: 'Bhutan', flag: '🇧🇹' },
    { code: '+961', name: 'Lebanon', flag: '🇱🇧' },
    { code: '+962', name: 'Jordan', flag: '🇯🇴' },
    { code: '+963', name: 'Syria', flag: '🇸🇾' },
    { code: '+964', name: 'Iraq', flag: '🇮🇶' },
    { code: '+965', name: 'Kuwait', flag: '🇰🇼' },
    { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+967', name: 'Yemen', flag: '🇾🇪' },
    { code: '+968', name: 'Oman', flag: '🇴🇲' },
    { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: '+972', name: 'Israel', flag: '🇮🇱' },
    { code: '+973', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+974', name: 'Qatar', flag: '🇶🇦' },
    { code: '+20', name: 'Egypt', flag: '🇪🇬' },
    { code: '+212', name: 'Morocco', flag: '🇲🇦' },
    { code: '+213', name: 'Algeria', flag: '🇩🇿' },
    { code: '+216', name: 'Tunisia', flag: '🇹🇳' },
    { code: '+218', name: 'Libya', flag: '🇱🇾' },
    { code: '+27', name: 'South Africa', flag: '🇿🇦' },
    { code: '+234', name: 'Nigeria', flag: '🇳🇬' },
    { code: '+254', name: 'Kenya', flag: '🇰🇪' },
    { code: '+256', name: 'Uganda', flag: '🇺🇬' },
    { code: '+255', name: 'Tanzania', flag: '🇹🇿' },
    { code: '+250', name: 'Rwanda', flag: '🇷🇼' },
    { code: '+251', name: 'Ethiopia', flag: '🇪🇹' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' },
    { code: '+64', name: 'New Zealand', flag: '🇳🇿' },
    { code: '+55', name: 'Brazil', flag: '🇧🇷' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: '+56', name: 'Chile', flag: '🇨🇱' },
    { code: '+57', name: 'Colombia', flag: '🇨🇴' },
    { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
    { code: '+51', name: 'Peru', flag: '🇵🇪' },
    { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
    { code: '+591', name: 'Bolivia', flag: '🇧🇴' },
    { code: '+595', name: 'Paraguay', flag: '🇵🇾' },
    { code: '+598', name: 'Uruguay', flag: '🇺🇾' },
    { code: '+52', name: 'Mexico', flag: '🇲🇽' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private ordersService: OrdersService,
    private shippingConfigService: ShippingConfigService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.shippingConfigService.getCountries().pipe(take(1)).subscribe((countries) => {
      this.availableCountries = countries;
      this.detectUserCountry();
    });
    this.quotationForm.get('country')?.valueChanges.subscribe(() => {
      this.syncPhoneCountryWithAddress();
    });
    // Dynamically require otherTitle when 'Other' is selected
    this.quotationForm.get('title')?.valueChanges.subscribe((value) => {
      const otherTitleControl = this.quotationForm.get('otherTitle');
      if (!otherTitleControl) return;
      if (value === 'Other') {
        otherTitleControl.setValidators([Validators.required]);
      } else {
        otherTitleControl.clearValidators();
        otherTitleControl.setValue('');
      }
      otherTitleControl.updateValueAndValidity();
    });
  }

  initializeForm(): void {
    this.quotationForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      otherTitle: [''],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      phone: ['', [Validators.required]],
      country: ['', [Validators.required]],
      companyName: [''],
      taxId: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      question: ['', [Validators.required]],
      termsAgreement: [false, Validators.requiredTrue],
    });
  }

  closeModal(): void {
    this.resetForm();
    this.closeModalEmitter.emit();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB in bytes
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    const availableSlots = Math.max(0, 5 - this.selectedFiles.length);
    if (validFiles.length > availableSlots) {
      alert('You can attach a maximum of 5 files.');
    }
    this.selectedFiles = [
      ...this.selectedFiles,
      ...validFiles.slice(0, availableSlots),
    ];
    input.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  getFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onSubmit(): void {
    if (this.quotationForm.valid) {
      this.isSubmitting = true;

      // Get form values and update phone with country code
      const formValues = { ...this.quotationForm.value };
      formValues.phone = this.getFullPhoneNumber();

      const quotationPayload = new FormData();
      const fields: Record<string, string> = {
        title: formValues.title === 'Other' ? (formValues.otherTitle || 'Other') : formValues.title,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phone: formValues.phone,
        country: formValues.country,
        companyName: formValues.companyName || '',
        taxId: formValues.taxId,
        email: formValues.email,
        question: formValues.question,
        productName: this.product?.name || '',
        productLabel: this.product?.label || '',
        productCategory: this.product?.category?.label || '',
        productCode: this.product?.reference || this.product?.code || '',
      };
      for (const [key, value] of Object.entries(fields)) {
        quotationPayload.append(key, value);
      }
      const submittedFiles = [...this.selectedFiles];
      for (const file of submittedFiles) {
        quotationPayload.append('attachments', file, file.name);
      }

      // Call the API
      this.ordersService.createQuotationRequest(quotationPayload).subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          this.showSuccessMessage();
          this.resetForm();
          this.closeModal();

          // Emit success event
          this.submitQuotationEmitter.emit({
            success: true,
            data: response,
            formValue: formValues,
            product: this.product,
            files: submittedFiles
          });

          this.formSubmit.emit({
            success: true,
            data: response,
            formValue: formValues,
            product: this.product,
            files: submittedFiles
          });
        },
        error: (error) => {
          this.isSubmitting = false;
          alert('Error submitting your request. Please try again.');

          // Emit error event
          this.submitQuotationEmitter.emit({
            success: false,
            error: error,
            formValue: formValues,
            product: this.product,
            files: submittedFiles
          });

          this.formSubmit.emit({
            success: false,
            error: error,
            formValue: formValues,
            product: this.product,
            files: this.selectedFiles
          });
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.quotationForm.controls).forEach(key => {
        this.quotationForm.get(key)?.markAsTouched();
      });
    }
  }

  private resetForm(): void {
    this.quotationForm?.reset({ termsAgreement: false });
    this.selectedFiles = [];
    this.isSubmitting = false;
    this.showCountryDropdown = false;
    this.countrySearchTerm = '';
    this.isDeliveryCountryDropdownOpen = false;
    this.deliveryCountrySearchTerm = '';

    // Re-detect user location after form reset
    this.detectUserCountry();
  }

  private showSuccessMessage(): void {
    // You can implement a toast/notification service here
    alert('Your quotation request has been submitted successfully! We will contact you soon.');
  }

  // Country selection methods
  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) {
      this.countrySearchTerm = '';
    }
  }

  toggleDeliveryCountryDropdown(): void {
    this.isDeliveryCountryDropdownOpen = !this.isDeliveryCountryDropdownOpen;
    this.deliveryCountrySearchTerm = '';
    this.showCountryDropdown = false;
  }

  selectDeliveryCountry(country: ShippingCountry): void {
    this.quotationForm.get('country')?.setValue(country.code);
    this.isDeliveryCountryDropdownOpen = false;
    this.deliveryCountrySearchTerm = '';
  }

  getDeliveryCountryName(countryCode: string): string {
    return this.availableCountries.find(country => country.code === countryCode)?.name || 'Select a country...';
  }

  getFilteredDeliveryCountries(): ShippingCountry[] {
    const searchTerm = this.deliveryCountrySearchTerm.trim().toLowerCase();
    if (!searchTerm) {
      return this.availableCountries;
    }

    return this.availableCountries.filter(country =>
      country.name.toLowerCase().includes(searchTerm) ||
      country.code.toLowerCase().includes(searchTerm)
    );
  }

  getDeliveryCountryFlag(countryCode: string): string {
    if (!/^[A-Z]{2}$/i.test(countryCode)) {
      return '🌐';
    }

    return String.fromCodePoint(
      ...countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt(0))
    );
  }

  selectCountryCode(country: CountryCode): void {
    this.selectedCountryCode = country;
    this.showCountryDropdown = false;
    this.countrySearchTerm = '';

    // Update the phone field with the new country code
    const currentPhone = this.quotationForm.get('phone')?.value || '';
    const phoneWithoutCountryCode = this.removeCountryCodeFromPhone(currentPhone);
    this.quotationForm.patchValue({
      phone: phoneWithoutCountryCode
    });
  }

  syncPhoneCountryWithAddress(): void {
    const countryCode = this.quotationForm.get('country')?.value;
    if (!countryCode) {
      this.selectedCountryCode = {
        code: '',
        name: 'Select country',
        flag: '🌐',
      };
      return;
    }

    const phoneCountry = this.getCountryByCode(countryCode);
    this.selectedCountryCode = phoneCountry || {
      code: '',
      name: 'Select country',
      flag: '🌐',
    };
  }

  getFilteredCountries(): CountryCode[] {
    if (!this.countrySearchTerm) {
      return this.countries;
    }

    const searchTerm = this.countrySearchTerm.toLowerCase();
    return this.countries.filter(country =>
      country.name.toLowerCase().includes(searchTerm) ||
      country.code.includes(searchTerm)
    );
  }

  private removeCountryCodeFromPhone(phone: string): string {
    if (!phone) return '';

    // Remove any existing country code from the phone number
    for (const country of this.countries) {
      if (phone.startsWith(country.code)) {
        return phone.substring(country.code.length).trim();
      }
    }

    return phone;
  }

  getFullPhoneNumber(): string {
    const phone = this.quotationForm.get('phone')?.value || '';
    const dialCode = this.selectedCountryCode.code;
    return [dialCode, phone].filter(Boolean).join(' ');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const countrySelector = target.closest('.country-selector');
    const deliveryCountrySelector = target.closest('.delivery-country-selector');

    if (!countrySelector && this.showCountryDropdown) {
      this.showCountryDropdown = false;
      this.countrySearchTerm = '';
    }

    if (!deliveryCountrySelector && this.isDeliveryCountryDropdownOpen) {
      this.isDeliveryCountryDropdownOpen = false;
      this.deliveryCountrySearchTerm = '';
    }
  }

  // Auto-detect user's country based on location (public method for manual triggering)
  public async detectUserCountry(): Promise<void> {
    this.isDetectingLocation = true;

    try {
      // Try a simple and reliable service first
      await this.trySimpleDetection();
    } catch (error) {
      this.isDetectingLocation = false;
    }
  }

  private async trySimpleDetection(): Promise<void> {
    try {
      // Use a simple service that works well with CORS
      const response = await fetch('https://api.ipify.org?format=json');
      const ipData = await response.json();

      if (ipData.ip) {
        // Now get country info using the IP
        const geoResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
        const geoData = await geoResponse.json();

        if (geoData.country_code) {
          const detectedCountry = this.getCountryByCode(geoData.country_code);
          if (detectedCountry) {
            this.selectedCountryCode = detectedCountry;
            this.setCountryInForm(geoData.country_code);
            this.isDetectingLocation = false;
            return;
          }
        }
      }
    } catch (error) {
      // Simple detection failed, trying other methods
    }

    // Fallback to other methods
    await this.tryMultipleServices();
  }

  private async tryMultipleServices(): Promise<void> {
    // Try multiple IP geolocation services
    const services = [
      'https://ipapi.co/json/',
      'https://ipinfo.io/json',
      'https://api.ipdata.co?api-key=test'
    ];

    for (const serviceUrl of services) {
      try {
        const response = await fetch(serviceUrl);
        const data = await response.json();

        // Handle different API response formats
        let countryCode = data.country_code || data.country || data.countryCode;

        if (countryCode) {
          const detectedCountry = this.getCountryByCode(countryCode);

          if (detectedCountry) {
            this.selectedCountryCode = detectedCountry;
            this.setCountryInForm(countryCode);
            this.isDetectingLocation = false;
            return;
          }
        }
      } catch (serviceError) {
        continue;
      }
    }

    // If all IP services fail, try browser geolocation
    this.tryBrowserGeolocation();
  }

  private tryBrowserGeolocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;

            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await response.json();

            if (data.countryCode) {
              const detectedCountry = this.getCountryByCode(data.countryCode);
              if (detectedCountry) {
                this.selectedCountryCode = detectedCountry;
                this.setCountryInForm(data.countryCode);
                this.isDetectingLocation = false;
                return;
              }
            }
          } catch (error) {
          }

          // If reverse geocoding fails, try fallback methods
          this.detectCountryFromBrowser();
          this.isDetectingLocation = false;
        },
        (error) => {
          // Try fallback methods
          this.detectCountryFromBrowser();
          this.isDetectingLocation = false;
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    } else {
      this.detectCountryFromBrowser();
      this.isDetectingLocation = false;
    }
  }

  private setCountryInForm(countryCode: string): void {
    const upperCountryCode = countryCode.toUpperCase();
    const countryControl = this.quotationForm.get('country');
    const isAvailable = this.availableCountries.some(country => country.code === upperCountryCode);

    if (countryControl && isAvailable) {
      countryControl.setValue(upperCountryCode);
    }
  }

  private getCountryByCode(countryCode: string): CountryCode | null {
    // Map country codes to phone country codes
    const countryMapping: { [key: string]: string } = {
      'US': '+1',
      'CA': '+1',
      'GB': '+44',
      'FR': '+33',
      'DE': '+49',
      'IT': '+39',
      'ES': '+34',
      'NL': '+31',
      'BE': '+32',
      'CH': '+41',
      'AT': '+43',
      'PT': '+351',
      'GR': '+30',
      'NO': '+47',
      'SE': '+46',
      'DK': '+45',
      'FI': '+358',
      'IS': '+354',
      'IE': '+353',
      'RU': '+7',
      'PL': '+48',
      'CZ': '+420',
      'SK': '+421',
      'HU': '+36',
      'RO': '+40',
      'BG': '+359',
      'HR': '+385',
      'SI': '+386',
      'RS': '+381',
      'ME': '+382',
      'BA': '+387',
      'MK': '+389',
      'AL': '+355',
      'TR': '+90',
      'UZ': '+998',
      'KG': '+996',
      'TJ': '+992',
      'TM': '+993',
      'AZ': '+994',
      'GE': '+995',
      'AM': '+374',
      'UA': '+380',
      'BY': '+375',
      'LT': '+370',
      'LV': '+371',
      'EE': '+372',
      'MD': '+373',
      'JP': '+81',
      'KR': '+82',
      'CN': '+86',
      'HK': '+852',
      'MO': '+853',
      'TW': '+886',
      'SG': '+65',
      'MY': '+60',
      'TH': '+66',
      'VN': '+84',
      'PH': '+63',
      'ID': '+62',
      'IN': '+91',
      'PK': '+92',
      'BD': '+880',
      'LK': '+94',
      'NP': '+977',
      'BT': '+975',
      'LB': '+961',
      'JO': '+962',
      'SY': '+963',
      'IQ': '+964',
      'KW': '+965',
      'SA': '+966',
      'YE': '+967',
      'OM': '+968',
      'AE': '+971',
      'IL': '+972',
      'BH': '+973',
      'QA': '+974',
      'EG': '+20',
      'MA': '+212',
      'DZ': '+213',
      'TN': '+216',
      'LY': '+218',
      'ZA': '+27',
      'NG': '+234',
      'KE': '+254',
      'UG': '+256',
      'TZ': '+255',
      'RW': '+250',
      'ET': '+251',
      'AU': '+61',
      'NZ': '+64',
      'BR': '+55',
      'AR': '+54',
      'CL': '+56',
      'CO': '+57',
      'VE': '+58',
      'PE': '+51',
      'EC': '+593',
      'BO': '+591',
      'PY': '+595',
      'UY': '+598',
      'MX': '+52'
    };

    const phoneCode = countryMapping[countryCode.toUpperCase()];
    if (phoneCode) {
      return this.countries.find(country => country.code === phoneCode) || null;
    }

    return null;
  }

  // Simple fallback method using navigator language as country hint
  private detectCountryFromBrowser(): void {
    // Try to detect country from browser language
    const language = navigator.language || navigator.languages?.[0];

    if (language) {
      // Extract country code from language (e.g., 'en-US' -> 'US')
      const parts = language.split('-');
      if (parts.length > 1) {
        const countryFromLang = parts[1];

        const detectedCountry = this.getCountryByCode(countryFromLang);
        if (detectedCountry) {
          this.selectedCountryCode = detectedCountry;
          this.setCountryInForm(countryFromLang);
          return;
        }
      }
    }

    // If all else fails, try timezone-based detection
    this.detectCountryFromTimezone();
  }

  private detectCountryFromTimezone(): void {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Simple timezone to country mapping for major regions
      const timezoneCountryMap: { [key: string]: string } = {
        'Europe/Warsaw': 'PL',
        'Europe/Berlin': 'DE',
        'Europe/Paris': 'FR',
        'Europe/Madrid': 'ES',
        'Europe/Rome': 'IT',
        'Europe/London': 'GB',
        'America/New_York': 'US',
        'America/Los_Angeles': 'US',
        'America/Chicago': 'US',
        'America/Toronto': 'CA',
        'Australia/Sydney': 'AU',
        'Europe/Amsterdam': 'NL',
        'Europe/Brussels': 'BE',
        'Europe/Zurich': 'CH',
        'Europe/Vienna': 'AT',
        'Europe/Copenhagen': 'DK',
        'Europe/Stockholm': 'SE',
        'Europe/Oslo': 'NO',
        'Europe/Helsinki': 'FI'
      };

      const countryFromTimezone = timezoneCountryMap[timezone];
      if (countryFromTimezone) {
        const detectedCountry = this.getCountryByCode(countryFromTimezone);
        if (detectedCountry) {
          this.selectedCountryCode = detectedCountry;
          this.setCountryInForm(countryFromTimezone);
        }
      }
    } catch (error) {
    }
  }

}
